import { Button, Code, CopyButton, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { downloadText } from '@repletoryreach/sdk-utils/file';
import { useT } from '../i18n/I18nProvider';

export interface RevealOnceProps {
  /** The secret. Rendered verbatim; it is never shown again. */
  value: string;
  title: string;
  description: string;
  /** Filename for the download, without an extension. */
  filename: string;
  /** Called when the user confirms they have saved it. */
  onDismiss: () => void;
}

/**
 * A secret shown exactly once.
 *
 * `settings-api-keys.md` §4 item 1 and `settings-webhooks.md` §4 item 1 are
 * the same defect on two pages, and both reviews said to fix them together:
 * the value was rendered in a code block and the user was expected to select
 * it with a mouse. No copy button, no download, no acknowledgement, and no
 * success toast either, so the only signal that anything had been created was
 * this box appearing. Miss it or navigate away and the secret is gone for
 * good: an API key must be recreated, a webhook deleted and recreated (which
 * rotates the secret and breaks the receiver).
 *
 * Dismissal is deliberate, so a stray navigation cannot lose it.
 */
export function RevealOnce({ value, title, description, filename, onDismiss }: RevealOnceProps) {
  const t = useT();

  return (
    <Alert color="warning" title={title}>
      <Stack gap="sm">
        <Text role="bodySmall">{description}</Text>
        {/* `user-select: all` so one click selects the whole value, for
            anyone whose browser refuses the clipboard API. */}
        <Code block className="rr-reveal-once">{value}</Code>
        <Group gap="xs" wrap="wrap">
          <CopyButton value={value}>
            {({ copied, failed, copy }) => (
              <Button size="xs" onClick={copy}>
                {failed ? t('common.copyFailed') : copied ? t('common.copied') : t('action.copy')}
              </Button>
            )}
          </CopyButton>
          <Button
            size="xs"
            variant="default"
            onClick={() => downloadText(value, `${filename}-${Date.now()}.txt`, 'text/plain')}
          >
            {t('common.download')}
          </Button>
          <Button size="xs" variant="subtle" onClick={onDismiss}>
            {t('settings.apiKeys.savedIt')}
          </Button>
        </Group>
      </Stack>
    </Alert>
  );
}
